// qrcreate_ui.jsx
// Shared presentational primitives for the QR Create admin section, so the four
// managers (overview, users, codes, scans) stay consistent and compact. Mirrors
// navela_ui.jsx, with the section's own accent and the chart pieces this
// dashboard needs.
//
// ── Dataviz decisions, once, here ────────────────────────────────────────────
// Magnitude is length in ONE hue (sky-600 #0284c7 on a slate-100 track) — never
// a hue per bar, because ranking scan counts is a magnitude question, not an
// identity one. Every chart on this dashboard is single-series, so none carries
// a legend; the section title names the series. Two measures are never put on
// one pair of axes — the overview shows scans and signups as two separate
// charts rather than a dual-axis chart.
//
// Status is the one categorical encoding: plan and subscription state. Those
// colours (emerald / violet / amber / rose / slate) are validated
// colourblind-safe against each other and against the sky mark hue, and every
// badge carries its text label, so identity is never colour-alone.
//
// Headline numbers are stat tiles, not one-bar charts.

import { useState } from 'react';
import { X, Loader2, Inbox, ScanLine } from 'lucide-react';

// The single magnitude hue. Kept as a constant so no chart quietly invents
// another one.
export const MARK_HUE = '#0284c7';

// Modal — centered dialog with a header, scrollable body and optional footer.
export function Modal({ open, title, subtitle, onClose, children, footer, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm sm:items-center">
      <div className={`my-8 w-full ${wide ? 'max-w-4xl' : 'max-w-xl'} rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200`}>
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

// Field — labelled form control wrapper.
export function Field({ label, hint, required, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

// input/select styling shared across the forms.
export const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200';

// Primary / secondary / danger buttons.
export function Button({ variant = 'primary', className = '', children, ...rest }) {
  const variants = {
    primary: 'bg-sky-600 text-white hover:bg-sky-700 disabled:bg-sky-300',
    secondary: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 disabled:bg-rose-300',
    ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// Full-width loading row.
export function Loading({ label = 'Loading…' }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

// Empty state placeholder.
export function EmptyState({ title = 'Nothing here yet', hint, icon: Icon = Inbox, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-16 text-center">
      <Icon className="mb-3 h-10 w-10 text-slate-300" />
      <p className="text-sm font-semibold text-slate-600">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-xs text-slate-400">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ConfirmDialog — confirmation for a consequential action, built on Modal.
export function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', variant = 'danger', busy, onConfirm, onCancel }) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant={variant} onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-slate-600">{message}</div>
    </Modal>
  );
}

// Section — titled card wrapping one report or table.
export function Section({ title, subtitle, icon: Icon, right, children, className = '' }) {
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-sky-600" />}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

// ─── Formatting ──────────────────────────────────────────────────────────────

export const fmtInt = (n) => (n ?? 0).toLocaleString();

export const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export const fmtDay = (day) => {
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export const fmtMoney = (cents, currency = 'USD') => {
  const value = (cents ?? 0) / 100;
  try {
    return value.toLocaleString(undefined, { style: 'currency', currency });
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

export const fullName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || '(no name)';

// ─── Status encodings ────────────────────────────────────────────────────────
//
// The one categorical scale on the dashboard. Each entry ships with its label —
// a badge is never colour alone.

const STATUS_STYLES = {
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  trialing: 'bg-violet-50 text-violet-700 ring-violet-200',
  grace: 'bg-amber-50 text-amber-700 ring-amber-200',
  canceled: 'bg-slate-100 text-slate-600 ring-slate-200',
  expired: 'bg-rose-50 text-rose-700 ring-rose-200',
};

const STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trialing',
  grace: 'Grace period',
  canceled: 'Canceled',
  expired: 'Expired',
};

export function Badge({ children, className = '' }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${className}`}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  return (
    <Badge className={STATUS_STYLES[status] || 'bg-slate-100 text-slate-600 ring-slate-200'}>
      {STATUS_LABELS[status] || status || '—'}
    </Badge>
  );
}

// PlanBadge reports the DERIVED entitlement, not the stored plan column: an
// expired pro row is a free user, so showing "Pro" for it would be a lie the
// support team then has to unpick. When the two disagree the stored plan is
// still shown, quietly, so the row is explainable.
export function PlanBadge({ subscription }) {
  const entitledPro = !!subscription?.entitlements?.pro;
  const storedPlan = subscription?.plan || 'free';
  const mismatch = entitledPro !== (storedPlan === 'pro');

  return (
    <span className="inline-flex items-center gap-1">
      <Badge className={entitledPro ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-600 ring-slate-200'}>
        {entitledPro ? 'Pro' : 'Free'}
      </Badge>
      {mismatch && (
        <span className="text-[10px] font-medium text-slate-400" title={`Stored plan is "${storedPlan}", but entitlement has lapsed or not started`}>
          (plan: {storedPlan})
        </span>
      )}
    </span>
  );
}

// ─── Charts ──────────────────────────────────────────────────────────────────

// StatTile — a headline number. Not a chart, on purpose: a single value is a
// stat tile, never a one-bar bar chart.
export function StatTile({ label, value, hint, icon: Icon, loading, accent }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${accent ? 'border-sky-200 bg-sky-50/60' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${accent ? 'text-sky-600' : 'text-slate-300'}`} />}
      </div>
      {loading ? (
        <div className="mt-2 h-7 w-20 animate-pulse rounded bg-slate-100" />
      ) : (
        <p className="mt-1 text-2xl font-extrabold tabular-nums text-slate-800">
          {typeof value === 'number' ? fmtInt(value) : (value ?? '—')}
        </p>
      )}
      {hint && !loading && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// MagnitudeList — ranked categories as bar LENGTH in one hue. Values are direct
// -labelled, so the bar is a comparison aid and the number is still exact.
export function MagnitudeList({ items, emptyLabel = 'No data in this window', max: maxOverride, limit = 12 }) {
  const rows = (items || []).slice(0, limit);
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-slate-400">{emptyLabel}</p>;
  }
  const max = maxOverride || Math.max(...rows.map((r) => r.value || 0), 1);

  return (
    <ul className="space-y-2.5">
      {rows.map((item, i) => (
        <li key={`${item.label}-${i}`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium text-slate-700" title={item.label}>
              {item.label || '(unknown)'}
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-500">{fmtInt(item.value)}</span>
          </div>
          {item.sub && <p className="truncate text-xs text-slate-400">{item.sub}</p>}
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(2, ((item.value || 0) / max) * 100)}%`, backgroundColor: MARK_HUE }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// TrendBars — one series over time as columns, with a per-bar hover tooltip.
// Single series, so no legend: the section title names it.
export function TrendBars({ points, label = 'scans', emptyLabel = 'No activity in this window', height = 140 }) {
  const [hover, setHover] = useState(null);
  const data = points || [];

  if (data.length === 0) {
    return <p className="py-10 text-center text-sm text-slate-400">{emptyLabel}</p>;
  }

  const max = Math.max(...data.map((p) => p.count || 0), 1);
  const total = data.reduce((sum, p) => sum + (p.count || 0), 0);

  return (
    <div>
      <div className="relative">
        {hover && (
          <div
            className="pointer-events-none absolute z-20 -translate-x-1/2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg"
            style={{ left: `${hover.left}%`, bottom: `${height + 8}px` }}
          >
            <span className="block whitespace-nowrap font-semibold">{fmtInt(hover.count)} {label}</span>
            <span className="block whitespace-nowrap text-slate-300">{fmtDay(hover.day)}</span>
          </div>
        )}

        {/* 2px gaps keep adjacent bars from reading as one block. */}
        <div className="flex items-end gap-[2px]" style={{ height: `${height}px` }}>
          {data.map((p, i) => {
            const pct = ((p.count || 0) / max) * 100;
            const isHovered = hover?.index === i;
            return (
              <button
                key={p.day}
                type="button"
                onMouseEnter={() => setHover({ ...p, index: i, left: ((i + 0.5) / data.length) * 100 })}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover({ ...p, index: i, left: ((i + 0.5) / data.length) * 100 })}
                onBlur={() => setHover(null)}
                className="group relative flex h-full flex-1 items-end outline-none"
                aria-label={`${fmtDay(p.day)}: ${fmtInt(p.count)} ${label}`}
              >
                <span
                  className="w-full rounded-t transition-opacity"
                  style={{
                    height: `${Math.max(2, pct)}%`,
                    backgroundColor: MARK_HUE,
                    opacity: hover && !isHovered ? 0.45 : 1,
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>{fmtDay(data[0].day)}</span>
        <span className="font-medium text-slate-500">{fmtInt(total)} total</span>
        <span>{fmtDay(data[data.length - 1].day)}</span>
      </div>
    </div>
  );
}

// Meter — one ratio against its limit, in the same hue as every other magnitude
// mark. Used for the splash funnel and quota usage; never a two-slice pie.
export function Meter({ label, value, total, hint }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm font-semibold tabular-nums text-slate-600">
          {fmtInt(value)} / {fmtInt(total)} <span className="text-slate-400">({pct}%)</span>
        </span>
      </div>
      <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: MARK_HUE }} />
      </div>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// Stat — a compact figure inside a panel (smaller sibling of StatTile).
export function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-lg p-3 ${accent ? 'bg-sky-50' : 'bg-slate-50'}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(value)}</p>
    </div>
  );
}

// BreakdownBlock — one dimension of a scan rollup as a ranked magnitude list.
export function BreakdownBlock({ title, items }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <h5 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-600">{title}</h5>
      <MagnitudeList items={(items || []).map((b) => ({ label: b.label, value: b.count }))} limit={8} />
    </div>
  );
}

// ScanRollup renders a ScanStats payload from the backend. The same shape comes
// back for a code, a user and the whole product, so all three views render it
// with this one component.
//
// Nothing here is gated on the owner's plan — the app hides the location/device
// breakdowns behind Pro, but an operator looking at an account sees all of it.
// What it does NOT contain is an IP address: the backend never sends one.
export function ScanRollup({ stats }) {
  if (!stats) return <EmptyState icon={ScanLine} title="No scan analytics available" />;

  const splashTotal = (stats.splash_impressions || 0) + (stats.splash_redirects || 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={`Scans (${stats.days}d)`} value={stats.total_scans} accent />
        <Stat label="Days with scans" value={stats.unique_days} />
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">First scan</p>
          <p className="text-sm font-bold text-slate-800">{fmtDate(stats.first_scan_at)}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last scan</p>
          <p className="text-sm font-bold text-slate-800">{fmtDate(stats.last_scan_at)}</p>
        </div>
      </div>

      <div>
        <h5 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-600">Scans over time</h5>
        <TrendBars points={stats.timeline} label="scans" />
      </div>

      {splashTotal > 0 && (
        <Meter
          label="Splash screens that continued through"
          value={stats.splash_redirects || 0}
          total={splashTotal}
          hint="Free codes show an interstitial before redirecting; this is how many people carried on."
        />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <BreakdownBlock title="Devices" items={stats.devices} />
        <BreakdownBlock title="Operating systems" items={stats.operating_systems} />
        <BreakdownBlock title="Browsers" items={stats.browsers} />
        <BreakdownBlock title="Countries" items={stats.countries} />
        <BreakdownBlock title="Cities" items={stats.cities} />
        <BreakdownBlock title="Referrers" items={stats.referrers} />
        {(stats.top_codes || []).length > 0 && <BreakdownBlock title="Most scanned codes" items={stats.top_codes} />}
      </div>
    </div>
  );
}

// Pagination — shared footer for the paginated tables.
export function Pagination({ total, limit, offset, onChange, noun = 'row' }) {
  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
      <span>
        {fmtInt(total)} {noun}{total === 1 ? '' : 's'} · page {page} of {pageCount}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" className="px-3 py-1.5" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button variant="secondary" className="px-3 py-1.5" disabled={page >= pageCount} onClick={() => onChange(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}
